// ForgotPassword.jsx — Demande de réinitialisation (redesign éditorial)
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Mail, ArrowLeft, Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch {
      toast.error('Erreur, réessayez');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased grid place-items-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">

        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A] mb-10 transition-colors" data-testid="forgot-back-link">
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>

        <Link to="/" className="inline-flex items-center gap-2.5 mb-10 group">
          <Logo size={30} className="transition-transform group-hover:scale-105" />
          <span className="font-display font-black tracking-tight text-[#0F172A]">AssoPerf</span>
        </Link>

        {sent ? (
          <div className="bg-white border border-[#E2E8F0] rounded-3xl p-9 text-center" data-testid="forgot-success">
            <div className="w-14 h-14 rounded-2xl bg-[#059669]/10 grid place-items-center mx-auto mb-6">
              <CheckCircle2 className="w-7 h-7 text-[#059669]" />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#0F172A] mb-3 leading-tight">
              Email <span className="italic font-medium text-[#0047FF]">envoyé.</span>
            </h1>
            <p className="text-sm text-[#475569] leading-relaxed mb-7">
              Si un compte existe pour <strong className="text-[#0F172A]">{email}</strong>, vous recevrez un lien de réinitialisation dans quelques minutes.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#0F172A] text-[#0F172A] font-semibold text-sm transition-colors"
              data-testid="forgot-back-login"
            >
              Retour à la connexion
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-4">Récupération</div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02] mb-3">
                Mot de passe <span className="italic font-medium text-[#0047FF]">oublié ?</span>
              </h1>
              <p className="text-sm text-[#475569] leading-relaxed">
                Entrez votre email — nous vous envoyons un lien sécurisé pour le réinitialiser.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ap-input"
                  placeholder="vous@exemple.fr"
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#0047FF] hover:bg-[#0040E0] text-white font-bold text-base transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                data-testid="forgot-submit-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Envoyer le lien
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
